import { injectStyles } from "../../utils/core/injectStyles.js";
import { parseElementConfig, mergeAttributeMaps } from "../../utils/core/attributeParser.js";
import { AnimationStateManager, PerformanceOptimizer } from "../../utils/animation/animationStateManager.js";

const COMPONENT_CSS_ID = 'wb-3d-card-hover-styles';
const componentCSS = `
  /* FlowBitz - 3D Card Hover Effect Styles */
  .wb-hover3d__inner {
    transform-style: preserve-3d;
    will-change: transform;
    transition: transform 300ms linear;
    backface-visibility: hidden;
    transform-origin: center center;
    width: 100%;
    height: 100%;
  }
`;

function ensureStylesInjected() {
  injectStyles(COMPONENT_CSS_ID, componentCSS);
}

class Hover3DCard {
  constructor(root, config, instancesMap) {
    this.root = root;
    this.config = config;
    this.instancesMap = instancesMap;
    this.wrapper = null;
    this.boundMouseMove = null;
    this.boundMouseEnter = null;
    this.boundMouseLeave = null;
    this.componentName = '3d-card-hover';
    this.movedRootClasses = [];
    this.init();
  }

  init() {
    const tag = (this.root.tagName || '').toUpperCase();
    if (tag !== 'DIV' && tag !== 'BUTTON') {
      console.warn('3d-card-hover: Root element must be a <div> or <button>', this.root);
      return;
    }

    if (this.root.dataset.hover3dInitialized === 'true') return;

    ensureStylesInjected();

    // Get computed styles BEFORE modifying anything to capture width/height from CSS classes
    const computedStyle = window.getComputedStyle(this.root);
    const computedWidth = computedStyle.width;
    const computedHeight = computedStyle.height;
    
    // Get parent dimensions to detect if width/height are 100%
    const parent = this.root.parentElement;
    const parentWidth = parent ? window.getComputedStyle(parent).width : null;
    const parentHeight = parent ? window.getComputedStyle(parent).height : null;
    
    // Helper function to detect if a computed value represents 100%
    const is100Percent = (computedValue, parentValue) => {
      if (!parentValue || !computedValue) return false;
      const computed = parseFloat(computedValue);
      const parent = parseFloat(parentValue);
      // Allow 1px tolerance for rounding
      return Math.abs(computed - parent) <= 1;
    };
    
    // Determine the original CSS value format
    let rootWidth = null;
    let rootHeight = null;
    
    // Check inline styles first
    const originalStyle = this.root.getAttribute('style') || '';
    const trueOriginalStyle = originalStyle.replace(/perspective\s*:\s*[^;]+;?/gi, '').trim();
    const widthMatch = trueOriginalStyle.match(/width\s*:\s*([^;]+)/i);
    const heightMatch = trueOriginalStyle.match(/height\s*:\s*([^;]+)/i);
    const hasInlineWidth = widthMatch && widthMatch[1].trim();
    const hasInlineHeight = heightMatch && heightMatch[1].trim();
    
    if (hasInlineWidth) {
      rootWidth = hasInlineWidth; // Use inline value as-is
    } else if (computedWidth && computedWidth !== 'auto' && computedWidth !== '0px' && computedWidth !== '') {
      // Check if it's 100% by comparing with parent
      if (is100Percent(computedWidth, parentWidth)) {
        rootWidth = '100%'; // Preserve 100% format
      } else {
        rootWidth = computedWidth; // Use computed pixel value (e.g., 300px)
      }
    }
    
    if (hasInlineHeight) {
      rootHeight = hasInlineHeight; // Use inline value as-is
    } else if (computedHeight && computedHeight !== 'auto' && computedHeight !== '0px' && computedHeight !== '') {
      // Check if it's 100% by comparing with parent
      if (is100Percent(computedHeight, parentHeight)) {
        rootHeight = '100%'; // Preserve 100% format
      } else {
        rootHeight = computedHeight; // Use computed pixel value (e.g., 368px)
      }
    }

    // 1) Root hanya sebagai pembungkus (perspective saja)
    this.root.style.perspective = `${this.config.perspective}px`;

    // Simpan semua class asli root dan hapus dari root
    this.movedRootClasses = Array.from(this.root.classList);
    if (this.movedRootClasses.length) {
      this.root.className = ''; // Remove classes from root
    }

    // 2) Buat wrapper inner
    const wrapper = document.createElement('div');
    wrapper.classList.add('wb-hover3d__inner');
    
    // Move original classes to wrapper (removed from root)
    if (this.movedRootClasses.length) {
      wrapper.classList.add(...this.movedRootClasses);
    }

    // Keep ID on root element (with wb-component) - don't move it to wrapper
    // ID should stay on the element with wb-component attribute
    
    // Remove perspective, width, and height from styles that go to wrapper
    let cleanedStyle = trueOriginalStyle
      .replace(/width\s*:\s*[^;]+;?/gi, '')
      .replace(/height\s*:\s*[^;]+;?/gi, '')
      .trim();
    
    // Clean up any double semicolons or trailing/leading semicolons
    cleanedStyle = cleanedStyle.replace(/;;+/g, ';').replace(/^;|;$/g, '').trim();
    
    if (cleanedStyle) wrapper.setAttribute('style', cleanedStyle);
    
    // Set width/height on ROOT: preserve 100% format or use computed pixel values
    if (rootWidth) {
      this.root.style.width = rootWidth;
    }
    
    if (rootHeight) {
      this.root.style.height = rootHeight;
    }

    // Default transition jika belum diset
    if (!wrapper.style.transition) {
      wrapper.style.transition = this.config.transition;
    }

    // Optimasi performa
    PerformanceOptimizer.optimizeForAnimation(wrapper, {
      willChange: 'transform',
      backfaceVisibility: 'hidden',
      perspective: ''
    });

    // 3) Pindahkan children root → wrapper
    while (this.root.firstChild) {
      wrapper.appendChild(this.root.firstChild);
    }

    // 4) Sisipkan wrapper ke root
    this.root.appendChild(wrapper);
    this.wrapper = wrapper;

    // 5) Event pada wrapper
    const throttledMove = PerformanceOptimizer.throttle((e) => this.handleMouseMove(e), 16);
    this.boundMouseMove = (e) => throttledMove.call(this, e);
    this.boundMouseEnter = () => this.handleMouseEnter();
    this.boundMouseLeave = () => this.handleMouseLeave();

    this.wrapper.addEventListener('mousemove', this.boundMouseMove);
    this.wrapper.addEventListener('mouseenter', this.boundMouseEnter);
    this.wrapper.addEventListener('mouseleave', this.boundMouseLeave);

    this.root.dataset.hover3dInitialized = 'true';
  }

















  handleMouseEnter() {
    // State pada wrapper (root hanya pembungkus)
    AnimationStateManager.setAnimatingState(this.wrapper, 'wb-hover3d');
    AnimationStateManager.dispatchLifecycleEvent(this.root, 'start', this.componentName);
  }

  handleMouseMove(e) {
    if (!this.wrapper) return;
    const rect = this.wrapper.getBoundingClientRect();
    const dx = (e.clientX - rect.left - rect.width / 2);
    const dy = (e.clientY - rect.top - rect.height / 2);
    const x = dx / this.config.rotateDivisor;
    const y = -dy / this.config.rotateDivisor;
    this.wrapper.style.transform = `translateZ(0) rotateY(${x}deg) rotateX(${y}deg)`;


  }

  handleMouseLeave() {
    if (!this.wrapper) return;
    this.wrapper.style.transform = 'translateZ(0) rotateY(0deg) rotateX(0deg)';



    AnimationStateManager.setCompletedState(this.wrapper, 'wb-hover3d');
    AnimationStateManager.dispatchLifecycleEvent(this.root, 'complete', this.componentName);
  }

  destroy() {
    // Lepas listener dari wrapper
    if (this.boundMouseMove) this.wrapper?.removeEventListener('mousemove', this.boundMouseMove);
    if (this.boundMouseEnter) this.wrapper?.removeEventListener('mouseenter', this.boundMouseEnter);
    if (this.boundMouseLeave) this.wrapper?.removeEventListener('mouseleave', this.boundMouseLeave);

    // Kembalikan children ke root, kembalikan classes ke root
    if (this.wrapper) {
      PerformanceOptimizer.cleanupAfterAnimation(this.wrapper, { restoreOriginal: true });

      while (this.wrapper.firstChild) {
        this.root.appendChild(this.wrapper.firstChild);
      }

      // Restore classes to root (they were moved to wrapper)
      if (this.movedRootClasses.length) {
        this.root.classList.add(...this.movedRootClasses);
      }
      
      // Clean up wrapper styles
      this.wrapper.style.width = '';
      this.wrapper.style.height = '';

      // ID is already on root (never moved), so no need to restore

      this.root.removeChild(this.wrapper);
      this.wrapper = null;
    }

    delete this.root.dataset.hover3dInitialized;
  }
};

class CardHover3DAnimator {
  constructor() {
    this.componentName = '3d-card-hover';
    this.instances = new Map();
    this.defaultConfig = {
      perspective: 800,
      rotateDivisor: 15,
      transition: 'transform 300ms linear'
    };
    this.attributeMap = mergeAttributeMaps({
      perspective: { attribute: 'wb-perspective', type: 'number', parser: parseInt, min: 100, max: 5000 },
      rotateDivisor: { attribute: 'wb-rotate-divisor', type: 'number', parser: parseFloat, min: 1, max: 100 },
      transition: { attribute: 'wb-transition', type: 'string' }
    });
  };

  parseConfig(element) {
    return parseElementConfig(element, this.defaultConfig, this.attributeMap);
  };

  initElement(element) {
    if (this.instances.has(element)) return this.instances.get(element);
    const config = this.parseConfig(element);
    const instance = new Hover3DCard(element, config, this.instances);
    this.instances.set(element, instance);
    return instance;
  };

  initAll() {
    const elements = document.querySelectorAll('[wb-component="3d-card-hover"]');
    elements.forEach((el) => this.initElement(el));
  };

  refresh() { return this; };

  destroyAll() {
    this.instances.forEach((instance) => instance.destroy && instance.destroy());
    this.instances.clear();
  };
};

const cardHover3DAnimator = new CardHover3DAnimator();

document.addEventListener('DOMContentLoaded', () => {
  cardHover3DAnimator.initAll();
});

export default cardHover3DAnimator;